import { Clear, Notifications } from "@mui/icons-material";
import {
  Badge,
  Box,
  Card,
  Drawer,
  IconButton,
  List,
  styled,
  ThemeProvider,
  Typography
} from "@mui/material";
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { LoadingButton } from "@mui/lab";
import useNotification from "app/hooks/useNotification";
import useSettings from "app/hooks/useSettings";
import { sideNavWidth, topBarHeight } from "app/utils/global_constants";
import { getTimeDifference } from "app/utils/utils.js";
import { themeShadows } from "../MatxTheme/themeColors";
import { Small } from "../Typography";

const Notification = styled("div")(() => ({
  padding: "16px",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: topBarHeight,
  boxShadow: themeShadows[6],
  "& h5": {
    marginLeft: "8px",
    marginTop: 0,
    marginBottom: 0,
    fontWeight: "500"
  }
}));

const NotificationCard = styled(Box)(({ theme }) => ({
  position: "relative",
  "&:hover": {
    "& .messageTime": {
      display: "none"
    },
    "& .deleteButton": {
      opacity: "1"
    }
  },
  "& .messageTime": {
    color: theme.palette.text.secondary
  },
  "& .icon": { fontSize: "1.25rem" }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  opacity: "0",
  position: "absolute",
  right: 5,
  marginTop: 9,
  marginRight: "24px",
  background: "rgba(0, 0, 0, 0.01)"
}));

const CardLeftContent = styled("div")(({ theme }) => ({
  padding: "12px 0px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "rgba(0, 0, 0, 0.01)",
  "& small": {
    fontWeight: "500",
    color: theme.palette.text.secondary
  }
}));

const Heading = styled("span")(({ theme }) => ({
  fontWeight: "500",
  marginLeft: "16px",
  color: theme.palette.text.secondary
}));

export default function NotificationBar({ container }) {
  const { settings } = useSettings();
  const [panelOpen, setPanelOpen] = useState(false);
  const { deleteNotification, clearNotifications, notifications } = useNotification();
  const handleDrawerToggle = () => setPanelOpen(!panelOpen);
  return (
    <Fragment>
      <IconButton onClick={handleDrawerToggle}>
        <Badge color="secondary" badgeContent={notifications?.length}>
          <Notifications sx={{ color: "text.primary" }} />
        </Badge>
      </IconButton>

      <ThemeProvider theme={settings.themes[settings.activeTheme]}>
        <Drawer
          container={container}
          variant="temporary"
          anchor={"right"}
          open={panelOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
        >
          <Notification>
            <Box display={"flex"} alignItems={"center"}>
              <Notifications color="primary" />
              <Typography variant="subtitle1" fontWeight={"500"} sx={{ marginLeft: "8px" }}>
                Notifications
              </Typography>
            </Box>
            {!!notifications?.length && (
              <LoadingButton onClick={clearNotifications} variant="outlined" size="small">
                Clear All
              </LoadingButton>
            )}
          </Notification>
          <List sx={{ width: sideNavWidth }}>
            {notifications?.map((notification) => (
              <NotificationCard key={notification.jp_id}>
                <DeleteButton
                  size="small"
                  className="deleteButton"
                  onClick={() => deleteNotification(notification.jp_id)}
                >
                  <Clear className="icon" />
                </DeleteButton>

                <Box
                  // onClick={handleDrawerToggle}
                  sx={{ height: "inherit" }}
                  style={{ textDecoration: "none" }}
                >
                  <Card sx={{ mx: 2, mb: 3 }} elevation={3}>
                    <CardLeftContent>
                      <Box display="flex" mr={2}>
                        <Typography
                          noWrap={false}
                          sx={{ color: "black", marginLeft: "16px" }}
                          fontWeight={"500"}
                        >
                          {notification.jp_title}
                        </Typography>
                      </Box>

                      <Small className="messageTime">
                        {getTimeDifference(new Date(notification.createdAt))} ago
                      </Small>
                    </CardLeftContent>

                    <Box px={2} pt={1} pb={2}>
                      <Small color="text.secondary">{notification.jp_message}</Small>
                    </Box>
                  </Card>
                </Box>
              </NotificationCard>
            ))}
          </List>
        </Drawer>
      </ThemeProvider>
    </Fragment>
  );
}
